/**
 * 戦闘力サマリーの「要件定義」テーブルの行と合計を組み立てる純関数。
 *
 * 2026-10 の開発フロー変更で要件定義は開発ptとは別管理になった（fibo予算 `_PDG_v2` が
 * 「要件定義」と「開発pt（要件定義を除く）」を別行で持つ）。メンバー詳細の
 * 当月pt 内訳カード（`MemberPtBreakdownCard`）は **1 人分** の内訳を出すが、
 * 「いま誰が要件定義を持っていて、その人の実績のどれだけが要件定義か」を
 * チーム横断で見る口はどこにも無かった。ここはその横断版。
 *
 * pt は上の戦闘力テーブルの実績Pt と同じく **完了 / 処理中（現在スナップショット）を分けて**
 * 持ち、表示は `完了(処理中)` 形式。開発pt は「総実績 − 要件定義」で出す
 * （丸めた 2 値を足して合計とずれるのを避ける）。
 *
 * **行は要件定義ptを持つメンバーだけ、合計はチーム全員**という非対称がこのモジュールの肝。
 * 合計の総実績ptは上の戦闘力テーブルの合計（= チーム全員の実績Pt）と一致しなければならず、
 * 表示中の行だけを足すと「同じカードの 2 つの表で合計が違う」状態になる。
 *
 * 当月実績（要件定義 / 開発 / 総実績）に加えて、**待処理要件定義pt**（assign 済みで
 * status=未対応 の要件定義セット子票の予定pt）とその占有率も行に載せる。実績 3 列が
 * 「当月に何をしたか」なのに対し、こちらは「これから何を抱えているか」で、
 * `GET /combat-power/requirement-pending-resources` が現在スナップショットとして返す。
 * **未対応票を持つだけのメンバー（当月実績がまだ 0）も行に出す**: 「assign されたのに
 * 未着手」を見つけるのがこの列の目的で、実績 0 で行が消えるとその人が読めない。
 * 予定pt が 0（未見積り）の未対応票しか無いメンバーも同じ理由で行に出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力サマリーのメンバー月次データと待処理要件定義ptから、要件定義に関わるメンバーの行 (要件定義pt / 開発pt / 総実績pt を完了・処理中に分けて保持 + 要件定義割合 + 待処理ptと占有率) と、チーム全員を母数にした合計行を組み立てる
 * @graph-connects none
 */

import type {
  MonthlyCombatPower,
  RequirementPendingResource,
} from '../../../types/combat-power.js';

/** pt の完了 / 処理中内訳。表示は `完了(処理中)` 形式。 @graph-connects none */
export interface PtSplit {
  /** 当月の完了pt */
  completed: number;
  /** 処理中pt（現在スナップショット） */
  inProgress: number;
  /** 完了 + 処理中。割合の分子・分母に使う */
  total: number;
}

/**
 * 担当者名（小文字 nickName）→ 待処理要件定義pt（API レスポンスの 1 件そのまま）。
 *
 * 戦闘力データは nickName 基準なので、API の email ではなくニックネームで突き合わせる。
 * 明細（`tickets`）も一緒に持ち回るのは、行クリックで開くモーダルの「待処理」節が
 * **列の値と同じレスポンス**を使うことで合計のずれを構造的に防ぐため。
 * @graph-connects none
 */
export type RequirementPendingByName = ReadonlyMap<string, RequirementPendingResource>;

/** 要件定義テーブルの 1 行。 @graph-connects none */
export interface RequirementDefinitionRow {
  /** 担当者名 (nickName) */
  name: string;
  /** 要件定義セット子票の実績pt */
  requirement: PtSplit;
  /** 要件定義を除く開発pt (= 総実績 − 要件定義) */
  develop: PtSplit;
  /** 総実績pt (戦闘力サマリーの実績Pt 列と同一口径) */
  total: PtSplit;
  /** 総実績pt に対する要件定義pt の割合 (%)。総実績 0 のときは null */
  requirementPercent: number | null;
  /**
   * 待処理要件定義pt。現在スナップショットなので**当月を表示しているときだけ**値が入る
   * （呼出側が当月以外では `pendingByName` を渡さない）。該当なしは null で表示は `-`。
   */
  pending: RequirementPendingResource | null;
}

/** 要件定義テーブルの合計行。母数はチーム全員（要件定義ptを持たないメンバーも含む）。 @graph-connects none */
export interface RequirementDefinitionTotals extends Omit<
  RequirementDefinitionRow,
  'name' | 'pending'
> {
  /**
   * チーム全員の待処理要件定義pt 合計。**占有率の合計は持たない**: 分母（今日起点 1 ヶ月の
   * capacity）は待処理ptを持つメンバーの分しか取得していないため、チーム合計で割ると
   * 列見出しが言っている意味とは別の数になる。占有率は個人行だけで読む。
   */
  pendingPoint: number | null;
  /**
   * チーム全員が抱えている未対応の要件定義の**案件（母票）数**。
   * **行の案件数の単純和ではなく母票キーの重複排除**（同じ案件の別工程を複数人が持つことが
   * あるため、足し上げた数より少なくなる）。
   */
  pendingProjectCount: number | null;
}

/**
 * pt の丸め（小数第1位）。明細の pt を素直に足すと `12.299999999999999` のような
 * 浮動小数の端数が出て、割合の分母と表示値がわずかにずれる。
 * @graph-connects none
 */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * 数値として読めない pt を 0 に倒す。
 *
 * 型では必須の数値だが、**要件定義内訳はこの変更で API へ足したフィールド**なので、
 * フロントが先に出て API が古い（= フィールドを返さない）瞬間が起こり得る。素直に足すと
 * `undefined + undefined = NaN` になり、`NaN <= 0` は false なので「NaN pt の行」が
 * 表に並んでしまう。欠損は 0 として扱い、その月は行に出さない。
 * @graph-connects none
 */
function ptOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

/** 完了 / 処理中から PtSplit を組み立てる（丸めは表示粒度の小数第1位）。 @graph-connects none */
function split(completed: number, inProgress: number): PtSplit {
  return {
    completed: round1(completed),
    inProgress: round1(inProgress),
    total: round1(completed + inProgress),
  };
}

/** 総実績 − 要件定義 で開発pt の内訳を出す。 @graph-connects none */
function developSplit(total: PtSplit, requirement: PtSplit): PtSplit {
  return {
    completed: round1(total.completed - requirement.completed),
    inProgress: round1(total.inProgress - requirement.inProgress),
    total: round1(total.total - requirement.total),
  };
}

/** 割合（%、小数第1位）。分母 0 のときは null（表示は `-`）。 @graph-connects none */
function percentOf(part: number, whole: number): number | null {
  return whole > 0 ? round1((part / whole) * 100) : null;
}

/** メンバー 1 人の指定月の pt 内訳（要件定義 / 総実績）。backlogPt が無ければ null。 @graph-connects none */
function memberPt(
  member: MonthlyCombatPower,
  monthIdx: number,
): { requirement: PtSplit; total: PtSplit } | null {
  const bp = member.months[monthIdx]?.backlogPt;
  if (!bp) return null;
  return {
    requirement: split(ptOrZero(bp.requirementCompleted), ptOrZero(bp.requirementInProgress)),
    total: split(bp.completed, bp.inProgress),
  };
}

/** 空の pt 内訳（待処理ptだけを持つ = 当月実績がまだ 0 のメンバー用）。 @graph-connects none */
const ZERO_SPLIT: PtSplit = { completed: 0, inProgress: 0, total: 0 };

/**
 * 当月に要件定義の実績pt または待処理pt を持つメンバーの行を、要件定義pt の多い順に返す。
 *
 * 並び順を上の戦闘力テーブル（入社順）に合わせないのは、こちらは全員ではなく
 * **要件定義に関わっている人だけ** の抜き出しで行が飛ぶため、入社順にしても
 * 上下で突き合わせられないから。「いま誰が要件定義を多く持っているか」が読み筋なので
 * pt 降順（同値はニックネーム順）にする。
 *
 * 行に出す条件は「当月の要件定義実績pt > 0」**または**「待処理要件定義pt > 0」。後者を
 * 足すのは、assign されたのに未着手のメンバーは実績が 0 で、実績条件だけだと行が消えて
 * しまうため（その人を見つけるのが待処理列の目的）。
 *
 * @param pendingByName 待処理要件定義pt（現在スナップショット）。**当月を表示している
 *   ときだけ渡す**。過去月・未来月に渡すとその月の実績の隣に「いまの未対応」が並び、
 *   同じ行の数字が別の時点を指すことになる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 指定月の backlogPt 内訳と待処理要件定義ptから、要件定義に関わるメンバーの行 (要件定義 / 開発 / 総実績 の完了・処理中内訳・要件定義割合・待処理ptと占有率) を pt 降順で組み立てる
 * @graph-connects none
 */
export function buildRequirementDefinitionRows(
  members: readonly MonthlyCombatPower[],
  monthIdx: number,
  pendingByName?: RequirementPendingByName,
): RequirementDefinitionRow[] {
  const rows: RequirementDefinitionRow[] = [];
  for (const m of members) {
    const pt = memberPt(m, monthIdx);
    const pending = pendingByName?.get(m.name.toLowerCase()) ?? null;
    // 予定pt 0（未見積り）でも未対応票を持つなら行に出す。未対応の要件定義セット子票は
    // 予定pt 未入力のものが実データで多数あり、pt > 0 で絞ると「assign 済みで未着手」の
    // 大半が表から消える（API は未対応票を 1 件以上持つ担当者だけを返す）。
    const hasPending = pending !== null;
    // 要件定義に関わっていない人は出さない（全員を出すと上の戦闘力テーブルの再掲になる）
    if ((!pt || pt.requirement.total <= 0) && !hasPending) continue;
    const requirement = pt?.requirement ?? ZERO_SPLIT;
    const total = pt?.total ?? ZERO_SPLIT;
    rows.push({
      name: m.name,
      requirement,
      develop: developSplit(total, requirement),
      total,
      requirementPercent: percentOf(requirement.total, total.total),
      pending,
    });
  }
  rows.sort((a, b) =>
    b.requirement.total !== a.requirement.total
      ? b.requirement.total - a.requirement.total
      : a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );
  return rows;
}

/**
 * 合計行。**表示中の行ではなくチーム全員（`members` 全件）を母数にする**。
 *
 * 総実績ptが上の戦闘力テーブルの合計（チーム全員の実績Pt）と一致しないと、同じカードに
 * 並ぶ 2 つの表が違う「合計」を出すことになる。したがって開発ptの合計には、この表に
 * 行が出ていないメンバー（要件定義pt 0 の人）の開発ptも含まれる。割合も
 * 「チームの実績ptのうち要件定義が占める比率」になる（表示行だけの比率ではない）。
 *
 * 待処理要件定義ptの合計も同じくチーム全員が母数。案件数は母票キーの重複排除で数えるので、
 * **行の案件数を足し上げた数とは一致しない**（同じ案件の別工程を複数人が持つため）。
 * **占有率の合計は出さない**（分母である今日起点 1 ヶ月の capacity は待処理ptを持つメンバーの
 * 分しか取得していないため、チーム合計で割ると列見出しの意味と違う数になる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義テーブルの合計行を、要件定義ptの有無に関わらずチーム全員の実績pt・待処理ptから算出する (総実績ptは戦闘力テーブルの合計と一致。占有率の合計は出さない)
 * @graph-connects none
 */
export function sumRequirementDefinitionTotals(
  members: readonly MonthlyCombatPower[],
  monthIdx: number,
  pendingByName?: RequirementPendingByName,
): RequirementDefinitionTotals {
  let requirementCompleted = 0;
  let requirementInProgress = 0;
  let totalCompleted = 0;
  let totalInProgress = 0;
  let pendingPoint = 0;
  // 案件数は母票キーの重複排除で数える（同じ案件の別工程を複数人が持つことがある）
  const pendingParentKeys = new Set<string>();
  for (const m of members) {
    const pending = pendingByName?.get(m.name.toLowerCase());
    pendingPoint += pending?.pendingPoint ?? 0;
    for (const project of pending?.projects ?? []) {
      pendingParentKeys.add(project.parentIssueKey);
    }
    const pt = memberPt(m, monthIdx);
    if (!pt) continue;
    requirementCompleted += pt.requirement.completed;
    requirementInProgress += pt.requirement.inProgress;
    totalCompleted += pt.total.completed;
    totalInProgress += pt.total.inProgress;
  }
  const requirement = split(requirementCompleted, requirementInProgress);
  const total = split(totalCompleted, totalInProgress);
  return {
    requirement,
    develop: developSplit(total, requirement),
    total,
    requirementPercent: percentOf(requirement.total, total.total),
    // 当月以外（pendingByName 未指定）は待処理ptの時点が合わないので合計も出さない
    pendingPoint: pendingByName === undefined ? null : round1(pendingPoint),
    pendingProjectCount: pendingByName === undefined ? null : pendingParentKeys.size,
  };
}
